import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BrandMark } from '../BrandMark';
import { APP_NAME } from '../../../config/branding';
import { navigateToHome } from '../../../core/navigation/navigationRef';
import { getTabIconConfig, getTabLabel } from '../../../core/navigation/tabIconConfig';
import { useSampleData } from '../../../contexts/SampleDataContext';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { GlassSurface } from '../glass';
import { TabIcon } from '../icons/TabIcon';

/** Web desktop — vertical glass sidebar (replaces bottom tab bar). */
export function WebSidebar({ state, descriptors, navigation }: BottomTabBarProps) {
  const insets = useSafeAreaInsets();
  const { isSampleMode } = useSampleData();

  return (
    <View
      style={[
        styles.sidebar,
        {
          paddingTop: insets.top + spacing.lg,
          paddingBottom: insets.bottom + spacing.md,
        },
      ]}
    >
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={`${APP_NAME} home`}
        onPress={navigateToHome}
        style={({ pressed }) => [styles.brand, pressed && { opacity: 0.85 }]}
      >
        <BrandMark size={28} />
        <Text style={styles.brandName}>{APP_NAME}</Text>
      </Pressable>

      <View style={styles.nav}>
        {state.routes.map((route, index) => {
          const isFocused = state.index === index;
          const label = getTabLabel(route.name);
          const iconConfig = getTabIconConfig(route.name);
          const tint = isFocused ? colors.accent : colors.tabBarInactive;

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            });
            if (!isFocused && !event.defaultPrevented) {
              navigation.navigate(route.name);
            }
          };

          return (
            <Pressable
              key={route.key}
              accessibilityRole="button"
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={descriptors[route.key].options.tabBarAccessibilityLabel ?? label}
              onPress={onPress}
              style={({ pressed }) => [styles.navItem, pressed && styles.navItemPressed]}
            >
              {isFocused ? (
                <GlassSurface tint="neutral" rounded="md" style={styles.activeItem}>
                  <NavItemContent
                    iconConfig={iconConfig}
                    label={label}
                    tint={tint}
                    focused={isFocused}
                  />
                </GlassSurface>
              ) : (
                <NavItemContent
                  iconConfig={iconConfig}
                  label={label}
                  tint={tint}
                  focused={isFocused}
                />
              )}
            </Pressable>
          );
        })}
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>{isSampleMode ? 'Sample preview' : 'Your household'}</Text>
        <Text style={styles.footerSub}>
          {isSampleMode ? 'Sign in to upload your screenshots' : 'Synced to your account'}
        </Text>
      </View>
    </View>
  );
}

function NavItemContent({
  iconConfig,
  label,
  tint,
  focused,
}: {
  iconConfig: ReturnType<typeof getTabIconConfig>;
  label: string;
  tint: string;
  focused: boolean;
}) {
  return (
    <View style={styles.navItemInner}>
      {iconConfig ? <TabIcon name={iconConfig.web} color={tint} focused={focused} size={20} /> : null}
      <Text style={[styles.navLabel, focused && styles.navLabelFocused]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: 'rgba(11, 15, 20, 0.85)',
    backdropFilter: 'blur(24px) saturate(160%)',
    WebkitBackdropFilter: 'blur(24px) saturate(160%)',
    paddingHorizontal: spacing.sm,
    justifyContent: 'flex-start',
  } as object,
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.lg,
    cursor: 'pointer',
  } as object,
  brandName: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
  },
  nav: {
    flex: 1,
    gap: 4,
  },
  navItem: {
    borderRadius: 12,
    cursor: 'pointer',
  } as object,
  navItemPressed: {
    opacity: 0.85,
  },
  activeItem: {
    width: '100%',
  },
  navItemInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    minHeight: 44,
  },
  navLabel: {
    ...typography.body,
    fontSize: 14,
    color: colors.tabBarInactive,
    fontWeight: '500',
  },
  navLabelFocused: {
    color: colors.text,
    fontWeight: '600',
  },
  footer: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  footerText: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
  },
  footerSub: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 2,
    fontSize: 11,
  },
});
